/* MateTurbo low, medium and high sound samples. Demo sources and displayed dB are labelled in Liquid. */
(() => {
  const instances = new WeakMap();
  function mount(panel) {
    if (instances.has(panel)) return;
    const events = new AbortController();
    const listen = (target, type, fn) => target.addEventListener(type, fn, {signal:events.signal});
    const config = JSON.parse(panel.querySelector('[data-audio-config]')?.textContent || '{}');
    if (!config.samples) return;
    const play = panel.querySelector('[data-audio-play]');
    const label = panel.querySelector('[data-audio-play-label]');
    const feedback = panel.querySelector('[data-audio-feedback]');
    const wave = panel.querySelector('[data-wave-bars]');
    const audio = document.createElement('audio');
    audio.preload = 'metadata'; audio.volume = .65; audio.dataset.soundAudio = ''; panel.append(audio);
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    let mode = 'low', disposed = false, version = 0, animation = 0;
    let inView = false, lastPaint = 0, nextPaint = 0, flowPhase = 0;
    let activation = 0, strength = .326, restingPath = true;
    // Retain the traced silhouette. Motion is illustrative, not a measured audio spectrum.
    const spectrum = panel.querySelector('.sound-radial-wave');
    const instrument = panel.querySelector('.sound-orbit-instrument') || panel;
    const wavePath = wave?.matches('path') ? wave : wave?.querySelector('path');
    const originalWave = wavePath?.getAttribute('d') || '';
    const glowGroup = wavePath?.previousElementSibling;
    const glowPath = glowGroup?.matches('g[filter]') ? glowGroup.querySelector('path') : null;
    const originalGlow = glowPath?.getAttribute('d');
    const glowId = glowGroup?.getAttribute('filter')?.match(/url\(#(.+)\)/)?.[1];
    const glowFilter = glowId ? spectrum?.querySelector(`[id="${glowId}"]`) : null;
    const originalFilter = glowFilter ? Object.fromEntries(['x','y','width','height'].map(key=>[key,glowFilter.getAttribute(key)])) : null;
    // Give the moving glow room inside the same 800px drawing surface.
    if (glowFilter) Object.entries({x:0,y:0,width:800,height:800}).forEach(([key,value])=>glowFilter.setAttribute(key,value));
    const peaks = [...originalWave.matchAll(/M([\d.-]+)[ ,]([\d.-]+)L([\d.-]+)[ ,]([\d.-]+)/g)].map(match=>{
      const [x,y,ox,oy] = match.slice(1).map(Number), dx = ox-x, dy = oy-y;
      const length2 = dx*dx+dy*dy, dot = (x-400)*dx+(y-400)*dy;
      // Bound the outer endpoint; High must never clip against the SVG viewport.
      const room = 382*382-(x-400)**2-(y-400)**2;
      const maxScale = length2 ? (-dot+Math.sqrt(dot*dot+length2*room))/length2 : 1;
      return {x,y,dx,dy,maxScale};
    });
    const time = value => `${String(Math.floor((value || 0) / 60)).padStart(2,'0')}:${String(Math.floor((value || 0) % 60)).padStart(2,'0')}`;
    function restoreWave() {
      if (restingPath) return;
      wavePath?.setAttribute('d',originalWave);
      if (glowPath && originalGlow) glowPath.setAttribute('d',originalGlow);
      restingPath = true;
    }
    function drawWave() {
      if (!wavePath || !peaks.length) return;
      // Idle uses the same travelling ripple at a gentle, mode-independent amplitude.
      const amplitude = .11 + activation * (strength - .11);
      const path = peaks.map(({x,y,dx,dy,maxScale},i)=>{
        const ripple = .6*Math.sin(i*.43+flowPhase)+.28*Math.sin(i*.17-flowPhase*1.54)+.12*Math.cos(i*.81-flowPhase*.57);
        const pulse = Math.min(maxScale,1+amplitude*(.12+ripple));
        return `M${x} ${y}L${(x+dx*pulse).toFixed(2)} ${(y+dy*pulse).toFixed(2)}`;
      }).join('');
      wavePath.setAttribute('d',path);
      glowPath?.setAttribute('d',path);
      restingPath = false;
    }
    const canAnimate = () => !disposed && inView && !document.hidden && !reduced.matches;
    function animate(now) {
      animation = 0;
      if (!canAnimate()) return;
      if (now>=nextPaint) {
        const dt = lastPaint ? Math.min((now-lastPaint)/1000,.1) : 1/24;
        lastPaint = now;
        nextPaint = Math.max(nextPaint+1000/24,now);
        const ease = 1-Math.exp(-dt/.1);
        activation += ((audio.paused?0:1)-activation)*ease;
        strength += (.14+config.samples[mode].amplitude*.62-strength)*ease;
        flowPhase += dt*(1.4+activation*(3+strength*4.5));
        drawWave();
      }
      animation = requestAnimationFrame(animate);
    }
    function syncMotion() {
      if (canAnimate()) {
        panel.dataset.waveState = audio.paused?'breathing':'playing';
        if (!animation) {lastPaint=0;nextPaint=0;animation=requestAnimationFrame(animate);}
      } else {
        cancelAnimationFrame(animation);animation=0;lastPaint=0;nextPaint=0;
        panel.dataset.waveState = reduced.matches?'static':'suspended';
        if (reduced.matches) {
          restoreWave();activation=0;
          if (spectrum) {spectrum.style.removeProperty('transform');spectrum.style.removeProperty('opacity');}
        }
      }
    }
    function sync() {
      play.setAttribute('aria-pressed',String(!audio.paused));
      label.textContent=audio.paused?config.play:config.pause;
      play.setAttribute('aria-label',`${label.textContent} · ${config.samples[mode].label}`);
      panel.classList.toggle('is-playing',!audio.paused);
      panel.style.setProperty('--audio-progress',String(Number.isFinite(audio.duration)&&audio.duration>0?audio.currentTime/audio.duration:0));
      panel.querySelector('[data-audio-time]').textContent=time(audio.currentTime);
      panel.querySelector('[data-audio-duration]').textContent=Number.isFinite(audio.duration)?time(audio.duration):'--:--';
      syncMotion();
    }
    async function resume(token) {
      play.disabled=true;
      try {await audio.play(); if(disposed){audio.pause();return;}}
      catch {if(!disposed&&token===version)feedback.textContent=config.error;}
      finally {if(!disposed&&token===version){play.disabled=false;sync();}}
    }
    function select(key, keepPlaying=false) {
      const sample=config.samples[key];if(!sample)return;
      const token=++version;audio.pause();mode=key;audio.src=sample.url;audio.currentTime=0;
      play.disabled=false;feedback.textContent='';panel.dataset.audioMode=key;
      panel.querySelector('[data-audio-db]').textContent=String(sample.db);
      panel.querySelectorAll('[data-sound-mode]').forEach(button=>button.setAttribute('aria-pressed',String(button.dataset.soundMode===key)));
      sync();if(keepPlaying)resume(token);
    }
    listen(play,'click',()=>{if(!audio.paused){audio.pause();return;}if(audio.ended)audio.currentTime=0;resume(version);});
    panel.querySelectorAll('[data-sound-mode]').forEach(button=>listen(button,'click',()=>{
      if(button.dataset.soundMode!==mode)select(button.dataset.soundMode,!audio.paused);
    }));
    ['play','pause','ended','timeupdate','loadedmetadata'].forEach(type=>listen(audio,type,sync));
    listen(audio,'error',()=>{feedback.textContent=config.error;play.disabled=false;sync();});
    listen(document,'visibilitychange',()=>{if(document.hidden)audio.pause();syncMotion();});
    listen(reduced,'change',sync);
    listen(window,'pagehide',()=>audio.pause());
    const observer=new IntersectionObserver(entries=>{
      for (const entry of entries) {
        if(entry.target===instrument)inView=entry.isIntersecting;
        if(entry.target===panel&&!entry.isIntersecting)audio.pause();
      }
      syncMotion();
    },{threshold:0});observer.observe(panel);if(instrument!==panel)observer.observe(instrument);
    select(mode);
    instances.set(panel,()=>{
      disposed=true;version++;cancelAnimationFrame(animation);events.abort();observer.disconnect();
      audio.pause();audio.removeAttribute('src');audio.load();audio.remove();restoreWave();
      if (spectrum) ['transform','opacity','will-change','transform-origin'].forEach(key=>spectrum.style.removeProperty(key));
      if (glowFilter) Object.entries(originalFilter).forEach(([key,value])=>value===null?glowFilter.removeAttribute(key):glowFilter.setAttribute(key,value));
      delete panel.dataset.waveState;instances.delete(panel);
    });
  }
  const within=parent=>[...(parent.matches?.('[data-audio-console]')?[parent]:[]),...parent.querySelectorAll('[data-audio-console]')];
  within(document).forEach(mount);
  document.addEventListener('shopify:section:load',event=>within(event.target).forEach(mount));
  document.addEventListener('shopify:section:unload',event=>within(event.target).forEach(panel=>instances.get(panel)?.()));
})();
